import * as React from 'react';
import { Check } from 'lucide-react';
import { ApiError } from '@/lib/api/client';
import type { SecureLinkSession } from '@/lib/api/secure-links';
import { ClientCard } from './client-frame';

/** What every purpose page gets from the secure-link page. */
export interface LinkPageProps {
  /** The link, held in memory only; it goes in a header, never a URL. */
  token: string;
  session: SecureLinkSession;
  /**
   * Call with any failed request: a refused link (401) turns the page into
   * "This link isn't valid any more" and answers true; anything else false.
   */
  onInvalid: (err: unknown) => boolean;
}

/** A refused link, whatever the reason (the API never says which). */
export function isLinkRefused(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

/** The server's own words for a refused answer, or a plain fallback. */
export function problemText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'Too many tries. Wait a minute and try again.';
    if (err.status >= 400 && err.status < 500 && err.code !== 'schema_mismatch') {
      return err.message;
    }
  }
  return `${fallback} Check your connection and try again.`;
}

/** The centred card at the end (`.client-card.center-col`). */
export function DoneCard({
  title,
  children,
  meta,
}: {
  title: string;
  children: React.ReactNode;
  meta?: React.ReactNode;
}) {
  return (
    <ClientCard center>
      <Check className="h-7 w-7 text-posture-healthy" aria-hidden="true" />
      <h1 className="text-h1 font-semibold text-ink-900">{title}</h1>
      <p className="text-body text-ink-700">{children}</p>
      {meta ? <div className="text-meta text-ink-500">{meta}</div> : null}
    </ClientCard>
  );
}

/** Every refusal reads the same (T-0174): no reason, nothing from the engagement. */
export function LinkInvalidCard() {
  return (
    <ClientCard>
      <h1 className="mb-1 text-h1 font-semibold text-ink-900">This link isn’t valid any more</h1>
      <p className="text-body text-ink-700">
        It may have expired or been replaced. Reply to the email it came in for a new one.
      </p>
    </ClientCard>
  );
}
